import { businessDetailQueryOptions } from '@/api/queries/businesses';
import { menuItemsInfiniteQueryOptions } from '@/api/queries/menu-items';
import { ScreenHeader } from '@/components/layout/screen-header';
import { Rating } from '@/components/ui/rating-distribution';
import { useAppTheme } from '@/providers/theme-provider';
import { imageUrl } from '@/utils/image-url';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { router, useLocalSearchParams } from 'expo-router';
import { useMemo, useState } from 'react';
import { ActivityIndicator, Image, Pressable, ScrollView, Text, View } from 'react-native';

export function BusinessMenuItemsScreen() {
  const theme = useAppTheme();
  const { id } = useLocalSearchParams<{ id: string }>();
  const [failedIds, setFailedIds] = useState<Set<string>>(new Set());

  const { data: business } = useQuery(businessDetailQueryOptions(id || ''));

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(menuItemsInfiniteQueryOptions(id || ''));

  const allItems = useMemo(
    () => data?.pages.flatMap((page) => page.items) ?? [],
    [data],
  );

  return (
    <View className="flex-1 bg-background">
      <ScreenHeader
        title={business?.name ? `${business.name} Menu` : 'Menu'}
        showBackButton
      />

      <ScrollView showsVerticalScrollIndicator={false}>
        <View className="px-4 pt-4 pb-8">
          {allItems.length === 0 && !isFetchingNextPage && (
            <View className="items-center py-12">
              <Ionicons name="restaurant-outline" size={48} className="text-muted-foreground" />
              <Text className="text-muted-foreground mt-3 text-base">
                No menu items yet
              </Text>
            </View>
          )}

          {allItems.map((item) => (
            <Pressable
              key={item.id}
              onPress={() => router.push({
                pathname: '/(root)/menu-item/[id]',
                params: { id: item.id },
              })}
              className="flex-row items-center p-3 rounded-xl mb-2 bg-surface border border-border"
            >
              {item.coverPhotoUrl && !failedIds.has(item.id) ? (
                <Image
                  source={{ uri: imageUrl(item.coverPhotoUrl, 'thumbnail') }}
                  className="rounded-lg bg-surface"
                  style={{ width: 64, height: 64 }}
                  resizeMode="cover"
                  accessibilityLabel={`Photo of ${item.name}`}
                  onError={() => setFailedIds((prev) => new Set(prev).add(item.id))}
                />
              ) : (
                <View
                  className="rounded-lg bg-surface-secondary items-center justify-center"
                  style={{ width: 64, height: 64 }}
                >
                  <Ionicons name="image-outline" size={24} className="text-muted-foreground" />
                </View>
              )}

              <View className="flex-1 ml-3">
                <View className="flex-row items-center flex-1">
                  <Text
                    className="font-semibold text-base text-foreground"
                    numberOfLines={1}
                    style={{ flexShrink: 1 }}
                  >
                    {item.name}
                  </Text>
                </View>
                {item.description && (
                  <Text
                    className="text-sm mt-0.5 text-muted-foreground"
                    numberOfLines={2}
                  >
                    {item.description}
                  </Text>
                )}
                {(item.checkInCount ?? 0) > 0 && (
                  <Text className="text-xs mt-1 text-muted-foreground">
                    {item.checkInCount} {item.checkInCount === 1 ? 'check-in' : 'check-ins'}
                  </Text>
                )}
              </View>

              <View className="items-end ml-2">
                <Rating
                  size="md"
                  distribution={item.ratingDistribution ?? null}
                />
              </View>
              <Ionicons name="chevron-forward" size={18} className="text-muted-foreground" style={{ marginLeft: 8 }} />
            </Pressable>
          ))}

          {hasNextPage && (
            <View className="py-4 items-center">
              {isFetchingNextPage ? (
                <ActivityIndicator color={theme.colors.primary} />
              ) : (
                <Pressable
                  onPress={() => fetchNextPage()}
                  className="px-6 py-3 rounded-lg bg-surface-secondary"
                  accessibilityRole="button"
                  accessibilityLabel="Load more menu items"
                >
                  <Text className="font-semibold text-foreground">
                    Load more
                  </Text>
                </Pressable>
              )}
            </View>
          )}
        </View>
      </ScrollView>
    </View>
  );
}
